class BuyXGetYHandler {
  static PROPERTY = '_buy_x_get_y';
  static BLOCK_KEY_PREFIX = 'tatcha:buyXGetY:blocked:';
  static UNAVAILABLE_MESSAGE = 'Your free gift is currently out of stock.';

  static init() {
    const configured = window.tatcha?.buyXGetYPromos;
    if (!Array.isArray(configured) || !configured.length) return;

    this.promos = configured
      .filter((promo) => promo?.id && promo.rewardVariantId && promo.qualifyingVariantIds?.length)
      .map((promo) => ({
        id: String(promo.id),
        rewardId: String(promo.rewardVariantId),
        rewardAvailable: promo.rewardAvailable !== false,
        rewardTitle: promo.rewardTitle || '',
        rewardMode: promo.rewardMode === 'per_item' ? 'per_item' : 'per_order',
        buyQuantity: Math.max(1, parseInt(promo.buyQuantity, 10) || 1),
        giftQuantity: Math.max(1, parseInt(promo.giftQuantity, 10) || 1),
        maxUsesPerOrder: Math.max(0, parseInt(promo.maxUsesPerOrder, 10) || 0),
        qualifyingIds: new Set(promo.qualifyingVariantIds.map(String))
      }));

    if (!this.promos.length) {
      console.warn('[BuyXGetY] no usable promos — each needs a reward product and trigger products.');
      return;
    }

    this.isCartPage = window.themeVariables?.settings?.pageType === 'cart';

    this._evaluating = false;
    this._pendingEvaluate = false;
    this._oosNotified = new Set();
    this._busyDepth = 0;
    this._preLock = false;
    this._selfDispatch = false;

    document.addEventListener('cart:change', (event) => {
      if (this._selfDispatch) return;
      this.evaluate(event.detail?.cart);
    });
    document.addEventListener('cart:refresh-complete', (event) => {
      this.evaluate(event?.detail);
    });

    document.addEventListener('submit', (event) => this.interceptTriggerAdd(event), true);
    document.addEventListener('click', (event) => this.interceptCartChangeLink(event), true);
    document.addEventListener('change', (event) => this.interceptQuantityInput(event), true);

    document.addEventListener('line-item:will-change', (event) => {
      if (event.detail?.targetQuantity !== 0) return;
      if (this._preLock || !this.cartHasGift()) return;
      this._preLock = true;
      this.startBusy();
    });

    this.evaluate();
  }

  static async evaluate(cartFromEvent) {
    if (this._evaluating) {
      this._pendingEvaluate = true;
      return;
    }
    this._evaluating = true;

    try {
      const cart = cartFromEvent?.items ? cartFromEvent : await this.fetchCart();
      if (!cart?.items) return;
      this._cart = cart;

      this.promos.forEach((promo) => {
        if (this.countQualifying(cart, promo) === 0) this.setBlocked(promo, false);
      });

      const plan = this.buildPlan(cart);
      plan.unavailable.forEach((promo) => this.notifyUnavailable(promo));

      const hasUpdates = Object.keys(plan.updates).length > 0;
      if (!hasUpdates && !plan.adds.length) return;

      this.startBusy();
      let mutated = false;
      let latestCart = null;
      try {
        if (hasUpdates) {
          latestCart = await this.applyUpdates(plan.updates);
          if (latestCart) {
            mutated = true;
            if (!plan.adds.length) this.flagShortfalls(latestCart);
          }
        }
        if (plan.adds.length) {
          const addedCart = await this.addRewards(plan.adds);
          if (addedCart) {
            mutated = true;
            latestCart = addedCart;
          }
        }
      } finally {
        this.endBusy();
        if (mutated) this.finishMutation(latestCart);
      }
    } catch (error) {
      console.error('[BuyXGetY] evaluate failed:', error);
    } finally {
      this._evaluating = false;
      if (this._preLock) {
        this._preLock = false;
        this.endBusy();
      }
      if (this._pendingEvaluate) {
        this._pendingEvaluate = false;
        this.evaluate();
      }
    }
  }

  static buildPlan(cart) {
    const updates = {};
    const adds = [];
    const unavailable = [];

    cart.items.forEach((item) => {
      if (!item.properties?.[BuyXGetYHandler.PROPERTY]) return;
      const promo = this.promoForLine(item);
      if (promo && String(item.variant_id) === promo.rewardId) return;
      updates[item.key] = 0;
    });

    this.promos.forEach((promo) => {
      const needed = this.rewardTarget(cart, promo);
      const owned = this.ownedLines(cart, promo);

      owned.slice(1).forEach((extra) => {
        updates[extra.key] = 0;
      });

      const primary = owned[0] || null;
      if (primary) {
        const settled = this.isBlocked(promo) ? Math.min(needed, primary.quantity) : needed;
        if (primary.quantity !== settled) updates[primary.key] = settled;
        return;
      }

      if (needed === 0) return;
      if (this.isBlocked(promo)) return;
      if (!promo.rewardAvailable) {
        unavailable.push(promo);
        return;
      }

      adds.push({ promo, quantity: needed });
    });

    return { updates, adds, unavailable };
  }

  static rewardTarget(cart, promo) {
    const qualifying = this.countQualifying(cart, promo);
    if (qualifying < promo.buyQuantity) return 0;

    let awards = promo.rewardMode === 'per_item' ? Math.floor(qualifying / promo.buyQuantity) : 1;
    if (promo.maxUsesPerOrder > 0) awards = Math.min(awards, promo.maxUsesPerOrder);

    return awards * promo.giftQuantity;
  }

  static ownedLines(cart, promo) {
    return cart.items.filter(
      (item) =>
        String(item.variant_id) === promo.rewardId &&
        String(item.properties?.[BuyXGetYHandler.PROPERTY] || '') === promo.id
    );
  }

  static countQualifying(cart, promo) {
    return cart.items.reduce((sum, item) => {
      if (!promo.qualifyingIds.has(String(item.variant_id))) return sum;

      const props = item.properties || {};
      if (props[BuyXGetYHandler.PROPERTY]) return sum;
      if (props._is_cyos === 'true' || props._is_byos === 'true') return sum;
      if (props._is_cyos_gift === 'true' || props._is_byos_gift === 'true') return sum;
      if (props._gwp === 'true' || props._gwp_tier === 'true' || props._gwp_selector === 'true') return sum;
      if (props._is_sample === 'true' || props._fortune_type === 'prize') return sum;

      return sum + item.quantity;
    }, 0);
  }

  static cartHasGift() {
    return !!this._cart?.items?.some(
      (item) => !!item.properties?.[BuyXGetYHandler.PROPERTY]
    );
  }

  static promoForLine(item) {
    const owner = String(item.properties?.[BuyXGetYHandler.PROPERTY] || '');
    return this.promos.find((promo) => promo.id === owner) || null;
  }

  static interceptCartChangeLink(event) {
    const link = event.target?.closest?.(`a[href*="${window.Shopify.routes.root}cart/change"]`);
    if (!link) return;

    const url = new URL(link.href, window.location.origin);
    this.maybeBatchWithReward(event, url.searchParams.get('id'), parseInt(url.searchParams.get('quantity'), 10));

    if (event.defaultPrevented) link.setAttribute('aria-busy', 'true');
  }

  static interceptQuantityInput(event) {
    const input = event.target;
    if (!input?.matches?.('[data-line-key]')) return;

    this.maybeBatchWithReward(event, input.getAttribute('data-line-key'), parseInt(input.value, 10));
  }

  static maybeBatchWithReward(event, lineKey, targetQuantity) {
    if (!lineKey || Number.isNaN(targetQuantity)) return;

    const cart = this._cart;
    if (!cart?.items) return;

    const line = cart.items.find((item) => item.key === lineKey);
    if (!line || targetQuantity === line.quantity) return;
    if (!this.promos.some((promo) => promo.qualifyingIds.has(String(line.variant_id)))) return;

    const after = cart.items
      .map((item) => (item.key === lineKey ? { ...item, quantity: targetQuantity } : item))
      .filter((item) => item.quantity > 0);

    const { updates, adds } = this.buildPlan({ items: after });

    if (adds.length || !Object.keys(updates).length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    event.target?.closest?.('line-item')?.dispatchEvent(
      new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity } })
    );

    this.applyBatch({ ...updates, [lineKey]: targetQuantity });
  }

  static interceptTriggerAdd(event) {
    const form = event.target;
    if (!form?.matches?.('form[action*="/cart/add"]')) return;
    if (!form.closest?.('product-form')) return;

    const cart = this._cart;
    if (!cart?.items) return;

    const item = this.formToItem(form);
    if (!item) return;
    if (!this.promos.some((promo) => promo.qualifyingIds.has(item.id))) return;

    const after = [
      ...cart.items,
      { variant_id: item.id, quantity: item.quantity, properties: item.properties || {} }
    ];
    const rewards = this.rewardDeltas({ items: after });
    if (!rewards.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.submitWithRewards(form, event.submitter, item, rewards);
  }

  static formToItem(form) {
    const data = new FormData(form);
    const id = String(data.get('id') || '');
    if (!id) return null;

    const item = { id, quantity: parseInt(data.get('quantity'), 10) || 1 };

    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;

    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;

    return item;
  }

  static rewardDeltas(cart) {
    const deltas = [];

    this.promos.forEach((promo) => {
      if (this.isBlocked(promo) || !promo.rewardAvailable) return;

      const owned = this.ownedLines(cart, promo).reduce((sum, line) => sum + line.quantity, 0);
      const delta = this.rewardTarget(cart, promo) - owned;

      if (delta > 0) deltas.push({ promo, quantity: delta });
    });

    return deltas;
  }

  static async submitWithRewards(form, submitter, item, rewards) {
    if (submitter?.getAttribute('aria-busy') === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const showLoadingBar = submitter?.querySelector('button-content') === null;
    const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    if (showLoadingBar) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    }

    try {
      const rewardItems = rewards.map(({ promo, quantity }) => ({
        id: promo.rewardId,
        quantity,
        properties: { [BuyXGetYHandler.PROPERTY]: promo.id }
      }));

      let result = await this.postAddItems([item, ...rewardItems]);

      if (!result.ok) {
        console.warn('[BuyXGetY] combined add rejected, retrying without the reward:', result.json?.description || result.json?.message);
        const retry = await this.postAddItems([item]);
        if (retry.ok) {
          rewards.forEach(({ promo }) => {
            this.setBlocked(promo, true);
            this.notifyUnavailable(promo);
          });
        }
        result = retry;
      }

      if (result.ok) {
        await this.afterAdd(form, result.json);
      } else {
        this.afterAddFailed(form, result.json);
      }
    } catch (error) {
      console.error('[BuyXGetY] combined add failed:', error);
      this.afterAddFailed(form, {});
    } finally {
      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }

  static async postAddItems(items) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items, sections: this.bundledSections() })
    });

    return { ok: response.ok, json: await response.json().catch(() => ({})) };
  }

  static async afterAdd(form, json) {
    const settings = window.themeVariables?.settings || {};
    if (settings.cartType === 'page' || settings.pageType === 'cart') {
      window.location.href = `${window.Shopify.routes.root}cart`;
      return;
    }

    const cart = await this.fetchCart();
    cart.sections = json.sections;
    this._cart = cart;

    const items = json.items || [json];
    form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart } }));
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );

    if (settings.cartType === 'message') {
      const template = window.themeVariables?.strings?.addedToCart || '';
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: template.replace('{{product_title}}', items?.[0]?.title) }
        })
      );
    }
  }

  static afterAddFailed(form, json) {
    form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: json.message } }));
    document.dispatchEvent(
      new CustomEvent('toast:show', { detail: { message: json.message, tone: 'error' } })
    );
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  static async applyBatch(updates) {
    this.startBusy();
    try {
      const cart = await this.applyUpdates(updates);

      if (!cart) {
        console.warn('[BuyXGetY] batched removal failed — reloading to resync.');
        window.location.reload();
        return;
      }

      this.finishMutation(cart);
    } catch (error) {
      console.error('[BuyXGetY] batched removal failed:', error);
      window.location.reload();
    } finally {
      this.endBusy();
    }
  }

  static async addRewards(adds) {
    let sections = null;
    let added = false;

    for (const add of adds) {
      const json = await this.postAdd(add);
      if (json) {
        added = true;
        sections = json.sections || sections;
      }
    }

    if (!added) return null;

    const cart = await this.fetchCart();
    const rolledBack = await this.rollBackFullPriceRewards(cart);

    if (rolledBack) return this.fetchCartWithSections();

    if (cart && sections) cart.sections = sections;
    return cart;
  }

  static async postAdd({ promo, quantity }) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [
          {
            id: promo.rewardId,
            quantity,
            properties: { [BuyXGetYHandler.PROPERTY]: promo.id }
          }
        ],
        sections: this.bundledSections()
      })
    });

    if (response.ok) return response.json().catch(() => ({}));

    const error = await response.json().catch(() => ({}));
    console.warn(
      `[BuyXGetY] add failed for "${promo.rewardTitle || promo.rewardId}":`,
      error.description || error.message || response.status
    );
    this.notifyUnavailable(promo);
    this.setBlocked(promo, true);
    return null;
  }

  static async rollBackFullPriceRewards(cart) {
    const offenders = (cart?.items || []).filter(
      (item) =>
        item.properties?.[BuyXGetYHandler.PROPERTY] &&
        item.original_line_price > 0 &&
        item.final_line_price >= item.original_line_price
    );

    if (!offenders.length) return false;

    await this.applyUpdates(Object.fromEntries(offenders.map((item) => [item.key, 0])));

    offenders.forEach((item) => {
      const promo = this.promoForLine(item);
      if (promo) this.setBlocked(promo, true);
      console.warn(
        `[BuyXGetY] rolled back "${item.product_title}" — the line was full price. The Get Y ` +
          'product should be priced at $0, or covered by an active Buy X get Y discount ' +
          'that combines with the other discounts in this cart.'
      );
    });

    return true;
  }

  static async applyUpdates(updates) {
    if (!Object.keys(updates).length) return null;

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections: this.bundledSections() })
    });

    return response.ok ? response.json() : null;
  }

  static bundledSections() {
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return [...sections].join(',');
  }

  static async fetchCartWithSections() {
    const ids = this.bundledSections().split(',').filter(Boolean);
    const cart = await this.fetchCart();
    if (!cart || !ids.length) return cart;

    const rendered = await Promise.all(
      ids.map((id) =>
        fetch(`${window.Shopify.routes.root}?section_id=${id}`)
          .then((response) => (response.ok ? response.text() : null))
          .catch(() => null)
      )
    );

    const sections = {};
    ids.forEach((id, index) => {
      if (rendered[index]) sections[id] = rendered[index];
    });

    if (Object.keys(sections).length) cart.sections = sections;
    return cart;
  }

  static flagShortfalls(cart) {
    if (!cart?.items) return;

    this.promos.forEach((promo) => {
      if (this.isBlocked(promo)) return;

      const target = this.rewardTarget(cart, promo);
      if (target === 0) return;
      const held = this.ownedLines(cart, promo).reduce((sum, item) => sum + item.quantity, 0);

      if (held >= target) return;

      this.setBlocked(promo, true);
      console.warn(
        `[BuyXGetY] "${promo.rewardTitle || promo.rewardId}" could not reach ${target} unit(s) — ` +
          `stock allowed ${held}. Holding there for this session.`
      );
    });
  }

  static finishMutation(cart) {
    if (this._preLock) {
      this._preLock = false;
      this.endBusy();
    }

    if (this.isCartPage) {
      window.location.reload();
      return;
    }

    const sections = cart?.sections;
    if (sections && Object.keys(sections).length) {
      this._cart = cart;
      this._selfDispatch = true;
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'line-item:change', cart }
          })
        );
      } finally {
        this._selfDispatch = false;
      }
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  static isBlocked(promo) {
    try {
      return sessionStorage.getItem(BuyXGetYHandler.BLOCK_KEY_PREFIX + promo.id) === '1';
    } catch (error) {
      return false;
    }
  }

  static setBlocked(promo, blocked) {
    try {
      const key = BuyXGetYHandler.BLOCK_KEY_PREFIX + promo.id;
      if (blocked) {
        sessionStorage.setItem(key, '1');
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (error) {
    }
  }

  static notifyUnavailable(promo) {
    if (this._oosNotified.has(promo.id)) return;
    this._oosNotified.add(promo.id);

    const message = promo.rewardTitle
      ? `${promo.rewardTitle} is out of stock, so it could not be added as your free gift.`
      : BuyXGetYHandler.UNAVAILABLE_MESSAGE;

    document.dispatchEvent(
      new CustomEvent('toast:show', { detail: { message, tone: 'error', duration: 6000 } })
    );
  }

  static async fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    return response.json();
  }

  static startBusy() {
    this._busyDepth += 1;
    clearTimeout(this._busyTimer);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = true;
      button.dataset.buyXGetYLocked = '1';
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
      if (element.querySelector('.buy-x-get-y-checkout-overlay')) return;
      const overlay = document.createElement('div');
      overlay.className = 'buy-x-get-y-checkout-overlay';
      overlay.style.cssText = 'position:absolute;inset:0;z-index:9999;cursor:not-allowed;';
      element.style.position = 'relative';
      element.appendChild(overlay);
    });

    this._busyTimer = setTimeout(() => {
      this._busyDepth = 0;
      this._preLock = false;
      this.endBusy();
    }, 8000);
  }

  static endBusy() {
    this._busyDepth = Math.max(0, this._busyDepth - 1);
    if (this._busyDepth > 0) return;

    clearTimeout(this._busyTimer);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    document.querySelectorAll('button[data-buy-x-get-y-locked="1"]').forEach((button) => {
      button.disabled = false;
      delete button.dataset.buyXGetYLocked;
    });
    document.querySelectorAll('.buy-x-get-y-checkout-overlay').forEach((element) => element.remove());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => BuyXGetYHandler.init());
} else {
  BuyXGetYHandler.init();
}
